import React, {useState, useEffect} from "react";
import {
    Typography,
    Row,
    Col,
    Card,
    Form,
    InputNumber,
    Select,
    Checkbox,
    Button,
    Table,
    Tooltip,
    Radio
} from "antd";

const {Title} = Typography;
const {Option} = Select;

// Types

type RiskProfile = "Flat" | "Progressive" | "Tilt";
type BonusStructure = "Rolling" | "Rebate";

interface FormValues {
    riskProfile: RiskProfile;
    bonusStructure: BonusStructure;
    expense?: number;
    automate: boolean;
}

interface ResultRow {
    key: number;
    expense: string;
    breakEven: string;
    profitable: string;
}

// Constants

const riskProfiles: Record<RiskProfile, number> = {
    Flat: 0.015,
    Progressive: 0.020,
    Tilt: 0.025
};


const formatKRW = (value: number): string => `₩${Math.round(value * 1_000_000).toLocaleString()}`;

const rollingBonus = (amount: number): number => {
    if (amount > 600) return 0.014 * amount;
    if (amount > 300) return 0.013 * amount;
    if (amount > 150) return 0.012 * amount;
    if (amount > 50) return 0.011 * amount;
    if (amount > 20) return 0.01 * amount;
    return 0;
};

const findRequiredRolling = (houseEdge: number, targetNet: number): number => {
    for (let rolling = 20; rolling <= 5000; rolling += 5) {
        const grossWin = rolling * houseEdge;
        const bonus = rollingBonus(rolling);
        const net = grossWin - bonus;
        if (net >= targetNet) return rolling;
    }
    return -1;
};

const displayRolling = (rolling: number): string => {
    return rolling === -1 ? "Unachievable" : formatKRW(rolling);
};

const generateBetOptions = (targetWin: number, houseEdge: number, showLargeBets: boolean, icon: string): string => {
    const options = showLargeBets ? [5, 10, 15, 20] : [0.5, 1, 1.5, 2];
    const handsPerHour = 70;
    const handsPerShoe = 55;
    const minHands = 3 * handsPerShoe; // minimum 3 shoes required

    return options.map(bet => {
        let hands = Math.ceil(targetWin / (bet * houseEdge));
        hands = Math.max(hands, minHands); // Enforce minimum 3 shoes
        const hours = (hands / handsPerHour).toFixed(1);
        const shoes = hands / handsPerShoe;
        const shoeLow = Math.floor(shoes);
        const shoeHigh = Math.ceil(shoes);
        const shoeText = shoeLow === shoeHigh ? `minimum ${shoeLow} shoes` : `${shoeLow}~${shoeHigh} shoes`;
        return `${icon} ₩${bet}M × ${hands} hands (~${hours} hrs, ${shoeText})`;
    }).join("\n");
};

const ToolsPage: React.FC = () => {
    const [form] = Form.useForm<FormValues>();
    const [results, setResults] = useState<ResultRow[]>([]);
    const [bonusStructure, setBonusStructure] = useState<BonusStructure>("Rolling");
    const [showLargeBets, setShowLargeBets] = useState(false);

    useEffect(() => {
        if (bonusStructure === "Rebate") {
            form.setFieldsValue({riskProfile: "Flat"});
        }
    }, [bonusStructure, form]);

    const calculate = (values: FormValues): void => {
        const {riskProfile, expense = 0, automate} = values;
        const houseEdge = bonusStructure === "Rebate" ? riskProfiles["Flat"] : riskProfiles[riskProfile];

        const calculateRow = (expenseValue: number): ResultRow => {
            const targetProfit = 1.5;

            if (bonusStructure === "Rolling") {
                const breakEvenRolling = findRequiredRolling(houseEdge, expenseValue);
                const profitRolling = findRequiredRolling(houseEdge, expenseValue + targetProfit);

                return {
                    key: expenseValue,
                    expense: formatKRW(expenseValue),
                    breakEven: displayRolling(breakEvenRolling),
                    profitable: displayRolling(profitRolling)
                };
            } else {
                const breakEvenTarget = Math.max(1, expenseValue); // ensure some target even if expense is zero
                const profitTarget = breakEvenTarget + targetProfit;

                return {
                    key: expenseValue,
                    expense: formatKRW(expenseValue),
                    breakEven: generateBetOptions(breakEvenTarget, houseEdge, showLargeBets, "🟡"),
                    profitable: generateBetOptions(profitTarget, houseEdge, showLargeBets, "✅")
                };
            }
        };

        const rows: ResultRow[] = automate
            ? [1, 2, 3].map((e) => calculateRow(e))
            : [calculateRow(expense)];

        setResults(rows);
    };

    const columns = [
        {title: "Expenses", dataIndex: "expense", key: "expense", width: 110},
        {
            title: bonusStructure === "Rebate"
                ? "Break-even Point (Minimum Bet)"
                : "Break-even Point (required rolling)",
            dataIndex: "breakEven",
            width: 220,
            ellipsis: true,
            key: "breakEven",
            render: (text: string) => (
                <pre
                    style={{
                        whiteSpace: 'pre-wrap',
                        backgroundColor: '#f5f0e6', // light tan/beige for breakeven
                        padding: '8px',
                        borderRadius: '4px'
                    }}
                >
    {text}
  </pre>
            )
        },
        {
            title: bonusStructure === "Rebate"
                ? "Profitability Point (Minimum Bet)"
                : "Profitability Point (required rolling)",
            dataIndex: "profitable",
            key: "profitable",
            width: 220,
            ellipsis: true,
            render: (text: string) => (
                <pre
                    style={{
                        whiteSpace: 'pre-wrap',
                        backgroundColor: '#e6f7e6', // light green for profitability
                        padding: '8px',
                        borderRadius: '4px'
                    }}
                >
    {text}
  </pre>
            )
        }
    ];

    return (
        <Row justify="center" align="middle" style={{minHeight: "100vh", background: "#f5f5f5"}}>
            <Col xs={24} sm={22} md={16} lg={12}>
                <Card
                    bordered={false}
                    style={{
                        borderRadius: 16,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        maxWidth: "100%",
                        overflowX: "auto"
                    }}
                >
                    <Title level={2} style={{textAlign: "center"}}>VIP Baccarat Profit Calculator</Title>
                    <Row justify="center">
                        <Col>
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={calculate}
                                initialValues={{riskProfile: "Flat", bonusStructure: "Rolling", automate: false}}
                            >
                                <Form.Item
                                    label={
                                        <Tooltip title="Flat = safest player, Tilt = most aggressive and volatile">
                                            Player Risk Profile
                                        </Tooltip>
                                    }
                                    style={{width: "100%", maxWidth: 300}}
                                    name="riskProfile"
                                    rules={[{required: true}]}
                                >
                                    <Select disabled={bonusStructure === "Rebate"}>
                                        <Option value="Flat">Flat (1.5%)</Option>
                                        <Option value="Progressive">Progressive (2.0%)</Option>
                                        <Option value="Tilt">Tilt (2.5%)</Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item
                                    label={
                                        <Tooltip
                                            title="Rolling = rewards based on turnover. Rebate = refund 10% of loss above 20m KRW.">
                                            Bonus Structure
                                        </Tooltip>
                                    }
                                    style={{width: "100%", maxWidth: 300}}
                                    name="bonusStructure"
                                    rules={[{required: true}]}
                                >
                                    <Select
                                        onChange={(value: BonusStructure) => {
                                            setBonusStructure(value);
                                            if (value === "Rebate") {
                                                form.setFieldsValue({riskProfile: "Flat"});
                                            }
                                        }}
                                    >
                                        <Option value="Rolling">Rolling</Option>
                                        <Option value="Rebate">Rebate on Loss</Option>
                                    </Select>
                                </Form.Item>

                                {bonusStructure === "Rebate" && (
                                    <Form.Item label="Display Mode">
                                        <Radio.Group
                                            value={showLargeBets ? "large" : "standard"}
                                            onChange={(e) => setShowLargeBets(e.target.value === "large")}
                                        >
                                            <Radio.Button value="standard">Standard Min Bets</Radio.Button>
                                            <Radio.Button value="large">Show Large Min Bets</Radio.Button>
                                        </Radio.Group>
                                    </Form.Item>
                                )}

                                <Form.Item name="automate" valuePropName="checked">
                                    <Checkbox>Automate Expenses</Checkbox>
                                </Form.Item>
                                <Form.Item shouldUpdate={(prev, curr) => prev.automate !== curr.automate}>
                                    {({getFieldValue}) => (
                                        <Form.Item
                                            label="Expenses to Attract Player (in million KRW)"
                                            name="expense"
                                            rules={[{required: false}]}
                                        >
                                            <InputNumber
                                                min={0}
                                                style={{width: "100%", maxWidth: 300}}
                                                disabled={getFieldValue("automate")}
                                            />
                                        </Form.Item>
                                    )}
                                </Form.Item>
                                <Form.Item>
                                    <Button type="primary" htmlType="submit" block
                                            style={{width: "100%", maxWidth: 300}}>
                                        Calculate
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Col>
                    </Row>

                    {results.length > 0 && (
                        <div style={{overflowX: "auto"}}>
                            <Table
                                columns={columns}
                                dataSource={results}
                                pagination={false}
                                bordered
                                scroll={{x: "max-content"}}
                            />
                        </div>
                    )}
                </Card>
            </Col>
        </Row>
    );
};

export default ToolsPage;